// FassalkartAI - Error Boundary

import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';
import { db } from '../../firebase/config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // ChunkLoadError = lazy-loaded chunk returned 503/network error → auto-reload once
    if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk')) {
      const key = 'chunkReloadAt';
      const last = Number(sessionStorage.getItem(key) || 0);
      if (Date.now() - last > 10_000) {
        sessionStorage.setItem(key, Date.now());
        window.location.reload();
      }
      return { hasError: false, error: null, errorInfo: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk')) return;
    this.setState({ errorInfo: info });
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught:', error, info);
    }
    // Log to Firestore for production monitoring
    try {
      addDoc(collection(db, 'ki_errorLogs'), {
        message: error?.message || String(error),
        stack: error?.stack?.slice(0, 500) || '',
        componentStack: info?.componentStack?.slice(0, 500) || '',
        url: window.location.href,
        createdAt: serverTimestamp(),
      }).catch(() => {});
    } catch (_) {}
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px', p: 3 }}>
          <Paper elevation={0} sx={{ p: 4, maxWidth: 440, textAlign: 'center', border: '1px solid #ffcdd2', bgcolor: '#fff8f8' }}>
            <ErrorOutline sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
            <Typography variant="h6" fontWeight={600} gutterBottom>
              ⚠️ Something went wrong
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {this.props.fallbackMessage || 'This page failed to load. Please refresh or go home.'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  this.setState({ hasError: false, error: null, errorInfo: null });
                  window.location.reload();
                }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  this.setState({ hasError: false, error: null, errorInfo: null });
                  window.location.href = '/';
                }}
              >
                Home
              </Button>
            </Box>
            {process.env.NODE_ENV === 'development' && (
              <Box component="code" sx={{ display: 'block', mt: 3, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, fontSize: 10, textAlign: 'left', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 200, overflow: 'auto' }}>
                {this.state.error?.message}
                {this.state.errorInfo?.componentStack}
              </Box>
            )}
          </Paper>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
